<template>
    <div>
        <div class="text-left m-8 text-sm">
            <p class="mb-4">Please check the <a href="https://www.lshtm.ac.uk/study/courses/masters-degrees" target="_blank">entry requirements</a> for your selected course choices before completing this section.</p>
            <p>The programme(s) you are applying to requires work experience and/or essential maths skills.  Please use the space below to provide further details on how you meet essential requirements for your programme(s).  We encourage you to provide as much detail as possible.</p>
        </div>
        <h1 class="mx-8 text-green-600 text-lg text-left">Please type your work experience and/or mathematics skills here:</h1>
        <div class="mx-8 border border-gray-300 bg-gray-100 mb-8 p-4">


            <form @submit.prevent="update" @change="dirtyForm()" @keydown="dirtyForm">
                <input type="hidden" v-model="form.isWorkExperienceRequired" />
                <input type="hidden" v-model="form.isMathematicsSkillsRequired" />
                <div class="flex flex-col-2">
                    <div class="w-1/2  text-left">
                        <p class="pl-1">Work Experience:</p>
                    </div>
                    <div class="w-1/2 text-right pr-2">
                        <p >Word count: <span :class="(workExperienceWordCount> maximumWordCount)? 'bg-red-300' : ''">{{ workExperienceWordCount }}</span> / {{ maximumWordCount }}</p>
                    </div>
                </div>
                <textarea-input
                    v-model="form.workExperience"
                    class="w-full-i bg-green-200 padding-2 mb-8"
                    label=""
                    @update:modelValue="value = $event"
                />
                <div v-if="workExperienceWordCount > maximumWordCount" class="flex justify-end mt-2 -mb-4">
                    <p class="text-right bg-red-300 px-2">Your 'Work Experience' is too long. The maximum word count is {{ maximumWordCount }} words</p>
                </div>

                <div v-if="serverError && serverError.errors.workExperience" class="server-error">{{ serverError.errors.workExperience[0] }}</div>

                <div class="flex flex-col-2">
                    <div class="w-1/2  text-left">
                        <p class="pl-1">Mathematics Skills / Qualifications:</p>
                    </div>
                    <div class="w-1/2 text-right pr-2">
                        <p >Word count: <span :class="(mathematicsWordCount > maximumWordCount)? 'bg-red-300' : ''">{{ mathematicsWordCount }}</span> / {{ maximumWordCount }}</p>
                    </div>
                </div>
                <textarea-input
                    v-model="form.mathematicsSkills"
                    class="w-full-i bg-green-200 padding-2 mb-8"
                    label=""
                    @update:modelValue="value = $event"
                />
                <div v-if="mathematicsWordCount > maximumWordCount" class="flex justify-end mt-2 -mb-4">
                    <p class="text-right bg-red-300 px-2">Your 'Mathematics Skills' is too long. The maximum word count is {{ maximumWordCount }} words</p>
                </div>

                <div v-if="serverError && serverError.errors.mathematicsSkills" class="server-error">{{ serverError.errors.mathematicsSkills[0] }}</div>

                <div v-if="successMessage" class="success-message mt-4">{{ successMessage }}</div>

                <div class="flex justify-end">
                    <application-component-save-button :disabled="formDirty && canSubmit" class="relative w-full lg:w-1/3 flex btn-green ml-auto mt-8 md:inline-block text-center"
                                                       type="submit"
                    >
                        Save
                        <div v-if="loading" class="lds-ring-container absolute top-2">
                            <div class="lds-ring">
                                <div /><div /><div /><div />
                            </div>
                        </div>
                    </application-component-save-button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import ApplicationComponentSaveButton from '@/shared/ApplicationComponentSaveButton'
import TextareaInput from '@/shared/TextareaInput'

export default {
    components: {
        ApplicationComponentSaveButton,
        TextareaInput,
    },
    props: {
        applicationData: Object,
        applicationRef: String,
        programmeData: Array,
    },
    data() {
        return {
            maximumWordCount: 300,
            formDirty: false,
            loading: false,
            successMessage: '',
            serverError: '',
            form: {
                isWorkExperienceRequired: this.requireWorkExperience(),
                isMathematicsSkillsRequired: this.requireMathsSkills(),
                workExperience: this.applicationData.workExperience ?? '',
                mathematicsSkills: this.applicationData.mathematicsSkills ?? '',
            }
        }
    },
    computed: {
        workExperienceWordCount() {
            return this.computeWordCount('workExperience');
        },
        mathematicsWordCount() {
            return this.computeWordCount('mathematicsSkills');
        },
        canSubmit(){
            return this.workExperienceWordCount <= this.maximumWordCount && this.mathematicsWordCount <= this.maximumWordCount
        }
    },
    mounted() {
        this.maximumWordCount = process.env.VUE_APP_MAXIMUM_WORD_WXAM_COUNT || 300
    },
    methods: {
        computeWordCount(attr) {
            if (this.form === null || this.form === undefined || this.form[attr] === null || this.form[attr] === undefined)
            {
                return 0
            }
            return this.form[attr].match(/\s+/g)?.length || 0
        },
        update() {
            this.loading = true
            this.$store.dispatch('updateWorkExperienceAndMathsSkills', {
                form: this.form,
                applicationRef: this.applicationRef,
            })
                .then((response) => {
                    this.loading = false
                    this.successMessage = response.data
                    this.serverError = ''
                })
                .catch(error => {
                    this.loading = false
                    this.successMessage = ''
                    this.serverError = error.response.data
                })
            this.formDirty = false
        },
        dirtyForm() {
            this.formDirty = true
            this.successMessage = ''
        },
        requireWorkExperience() {
            if (this.programmeData === undefined || this.programmeData === null)
                return false;

            let filtered = this.programmeData.filter(programme => programme.requireWorkExperience);
            return filtered !== undefined && filtered !== null && filtered.length > 0;
        },
        requireMathsSkills() {
            if (this.programmeData === undefined || this.programmeData === null)
                return false;

            let filtered = this.programmeData.filter(programme => programme.requireMathsSkills);
            return filtered !== undefined && filtered !== null && filtered.length > 0;
        }
    },
}
</script>